import { useEffect, useRef } from "react";
import { $getNearestNodeFromDOMNode } from "lexical";
import { $isTableNode } from "@lexical/table";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

const MIN_COL_WIDTH = 48;
const EDGE_HOVER_PX = 6;

type DragState = {
    tableEl: HTMLTableElement;
    colIndex: number;
    startX: number;
    startWidth: number;
};

export function TableCellResizer() {
    const [editor] = useLexicalComposerContext();
    const dragRef = useRef<DragState | null>(null);
    const indicatorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const rootEl = editor.getRootElement();
        if (!rootEl) return;

        const indicator = document.createElement("div");
        indicator.className = "rte-col-resize-indicator";
        indicator.style.display = "none";
        document.body.appendChild(indicator);
        indicatorRef.current = indicator;

        const showIndicator = (x: number, top: number, height: number) => {
            indicator.style.display = "block";
            indicator.style.left = `${x}px`;
            indicator.style.top = `${top}px`;
            indicator.style.height = `${height}px`;
        };
        const hideIndicator = () => {
            indicator.style.display = "none";
        };

        const findNearestColumnEdge = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return null;
            const cell = target.closest("th, td") as HTMLTableCellElement | null;
            if (!cell) return null;
            const table = cell.closest("table") as HTMLTableElement | null;
            if (!table || !rootEl.contains(table)) return null;

            const cellRect = cell.getBoundingClientRect();
            const distFromRight = cellRect.right - e.clientX;
            const distFromLeft = e.clientX - cellRect.left;
            const lastColIdx = (cell.parentElement?.children.length ?? 1) - 1;

            if (distFromRight >= 0 && distFromRight <= EDGE_HOVER_PX && cell.cellIndex < lastColIdx) {
                return { table, cell, edgeX: cellRect.right };
            }
            if (distFromLeft >= 0 && distFromLeft <= EDGE_HOVER_PX && cell.cellIndex > 0) {
                const prevCell = cell.previousElementSibling as HTMLTableCellElement | null;
                if (prevCell) {
                    return { table, cell: prevCell, edgeX: cellRect.left };
                }
            }
            return null;
        };

        const onMouseMove = (e: MouseEvent) => {
            if (dragRef.current) {
                const { tableEl, colIndex, startX, startWidth } = dragRef.current;
                const newWidth = Math.max(MIN_COL_WIDTH, startWidth + e.clientX - startX);
                for (const row of Array.from(tableEl.rows)) {
                    const cellEl = row.cells[colIndex];
                    if (cellEl) cellEl.style.width = `${newWidth}px`;
                }
                const tableRect = tableEl.getBoundingClientRect();
                const firstRow = tableEl.rows[0];
                const newCellRect = firstRow.cells[colIndex].getBoundingClientRect();
                showIndicator(newCellRect.right, tableRect.top, tableRect.height);
                return;
            }
            const hit = findNearestColumnEdge(e);
            if (!hit) {
                hideIndicator();
                rootEl.style.cursor = "";
                return;
            }
            const tableRect = hit.table.getBoundingClientRect();
            showIndicator(hit.edgeX, tableRect.top, tableRect.height);
            rootEl.style.cursor = "col-resize";
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            const hit = findNearestColumnEdge(e);
            if (!hit) return;
            e.preventDefault();
            e.stopPropagation();

            const { table, cell } = hit;
            const headerRow = table.rows[0];
            const colIndex = cell.cellIndex;
            const startWidth =
                headerRow.cells[colIndex]?.getBoundingClientRect().width ??
                cell.getBoundingClientRect().width;

            dragRef.current = {
                tableEl: table,
                colIndex,
                startX: e.clientX,
                startWidth,
            };
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        };

        const onMouseUp = () => {
            if (!dragRef.current) {
                rootEl.style.cursor = "";
                return;
            }
            const { tableEl } = dragRef.current;

            editor.update(() => {
                const tableNode = $getNearestNodeFromDOMNode(tableEl);
                if (!tableNode || !$isTableNode(tableNode)) return;
                const headerRow = tableEl.rows[0];
                const widths = Array.from(headerRow.cells).map((c) =>
                    Math.round(c.getBoundingClientRect().width),
                );
                tableNode.setColWidths(widths);
            });

            dragRef.current = null;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            rootEl.style.cursor = "";
            hideIndicator();
        };

        rootEl.addEventListener("mousemove", onMouseMove);
        rootEl.addEventListener("mousedown", onMouseDown, true);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);

        return () => {
            rootEl.removeEventListener("mousemove", onMouseMove);
            rootEl.removeEventListener("mousedown", onMouseDown, true);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            indicator.remove();
            indicatorRef.current = null;
        };
    }, [editor]);

    return null;
}
